import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { Link, useLocation } from 'react-router-dom'
import { useWindowWidth } from '../hooks/useWindowWidth'

// Hash-Links zeigen immer auf die Homepage, damit sie auch von /shop und /video aus funktionieren.
const NAV_ITEMS = [
  { label: 'Features', href: '/#features' },
  { label: 'Preise', href: '/#preise' },
  { label: 'Demo', href: '/#demo' },
  { label: 'Video', to: '/video' },
  { label: 'Shop', to: '/shop' },
] as const

const MOBILE_TABS = [
  { label: 'Video', to: '/video' },
  { label: 'Shop', to: '/shop' },
] as const

const linkColor = 'rgba(232,234,246,0.55)'

export function MarketingNav() {
  const [scrolled, setScrolled] = useState(false)
  const isMobile = useWindowWidth() < 768
  const { pathname } = useLocation()
  const onHome = pathname === '/'

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <motion.nav
      style={{
        position: 'fixed',
        top: 0, left: 0, right: 0,
        zIndex: 50,
        transition: 'background 200ms ease, border-color 200ms ease, backdrop-filter 200ms ease',
        background: scrolled ? 'rgba(14,11,30,0.85)' : 'transparent',
        borderBottom: scrolled ? '1px solid rgba(101,79,111,0.25)' : '1px solid transparent',
        backdropFilter: scrolled ? 'blur(20px)' : 'none',
      }}
      initial={{ opacity: 0, y: -16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.1 }}
    >
      <div style={{
        maxWidth: 1200,
        margin: '0 auto',
        padding: isMobile ? '0 16px' : '0 32px',
        height: 64,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        {/* Logo */}
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none' }}>
          <div style={{
            width: 36, height: 36,
            background: 'linear-gradient(135deg, #FF6D00, #FF9A00)',
            borderRadius: 10,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 14px rgba(255,109,0,0.4)',
            flexShrink: 0,
          }}>
            <span style={{
              fontFamily: 'Plus Jakarta Sans, sans-serif',
              fontWeight: 900, fontSize: 16, color: 'white',
            }}>B</span>
          </div>
          <span style={{
            fontFamily: 'Plus Jakarta Sans, sans-serif',
            fontWeight: 700, fontSize: 15, color: 'white',
          }}>Candlear</span>
        </Link>

        {/* Center links — hidden on mobile */}
        <div style={{ display: isMobile ? 'none' : 'flex', alignItems: 'center', gap: 36 }}>
          {NAV_ITEMS.map((item) => {
            const active = 'to' in item && pathname === item.to
            const style: React.CSSProperties = {
              position: 'relative',
              fontFamily: 'Inter, sans-serif',
              fontSize: 14, fontWeight: 500,
              color: active ? 'white' : linkColor,
              textDecoration: 'none',
              transition: 'color 150ms ease',
            }
            const hover = {
              onMouseEnter: (e: React.MouseEvent<HTMLElement>) => (e.currentTarget.style.color = 'white'),
              onMouseLeave: (e: React.MouseEvent<HTMLElement>) => {
                if (!active) e.currentTarget.style.color = linkColor
              },
            }
            return 'to' in item ? (
              <Link key={item.label} to={item.to} style={style} aria-current={active ? 'page' : undefined} {...hover}>
                {item.label}
                {active && <ActiveDot />}
              </Link>
            ) : (
              <a key={item.label} href={item.href} style={style} {...hover}>
                {item.label}
              </a>
            )
          })}
        </div>

        {/* CTAs */}
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 12 }}>
          {/* Video + Shop nur mobil; auf Desktop stehen sie in der Mitte, Video links vom Shop */}
          {isMobile && MOBILE_TABS.map((item) => {
            const active = pathname === item.to
            return (
              <Link
                key={item.to}
                to={item.to}
                aria-current={active ? 'page' : undefined}
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 14, fontWeight: 600,
                  color: active ? '#FF9A00' : 'rgba(232,234,246,0.65)',
                  textDecoration: 'none',
                  padding: '8px 10px',
                }}
              >
                {item.label}
              </Link>
            )
          })}
          <Link
            to="/admin"
            style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: 14, fontWeight: 600,
              color: 'rgba(232,234,246,0.65)',
              textDecoration: 'none',
              padding: '8px 18px',
              borderRadius: 10,
              border: '1px solid rgba(101,79,111,0.4)',
              transition: 'border-color 150ms ease, color 150ms ease',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = 'rgba(101,79,111,0.7)'
              e.currentTarget.style.color = 'white'
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = 'rgba(101,79,111,0.4)'
              e.currentTarget.style.color = 'rgba(232,234,246,0.65)'
            }}
          >
            Anmelden
          </Link>
          {/* Hidden on mobile */}
          <a
            href={onHome ? '#start' : '/#start'}
            style={{
              fontFamily: 'Plus Jakarta Sans, sans-serif',
              fontSize: 14, fontWeight: 700,
              color: 'white',
              textDecoration: 'none',
              padding: '8px 20px',
              borderRadius: 10,
              background: '#FF6D00',
              boxShadow: '0 4px 16px rgba(255,109,0,0.35)',
              transition: 'background 150ms ease, box-shadow 150ms ease',
              display: isMobile ? 'none' : 'inline-block',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = '#FF8C00'
              e.currentTarget.style.boxShadow = '0 4px 24px rgba(255,109,0,0.5)'
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = '#FF6D00'
              e.currentTarget.style.boxShadow = '0 4px 16px rgba(255,109,0,0.35)'
            }}
          >
            Kostenlos starten →
          </a>
        </div>
      </div>
    </motion.nav>
  )
}

function ActiveDot() {
  return (
    <span style={{
      position: 'absolute',
      left: '50%', bottom: -10,
      width: 4, height: 4,
      marginLeft: -2,
      borderRadius: '50%',
      background: '#FF6D00',
      boxShadow: '0 0 8px rgba(255,109,0,0.8)',
    }} />
  )
}
